import { useCallback, FC, ReactNode, useEffect } from 'react'
import { useMountedState } from 'react-use'
import createReducerContext from '@g4g/utils/src/react/createReducerContext'
import type { RecipientSelectAction, RecipientSelectState } from '../provider/types'
import { useRecipient } from '../RecipientProvider'

const initialState: RecipientSelectState = {
  /**
   * Currently selected radio option id.
   */
  selectedRadioCosId: undefined,
  /**
   * Whether the SearchBar search is finished
   *
   * this includes the debounce time plus
   * the current refetch time to response
   *
   * @see https://github.com/streamich/react-use/blob/master/docs/useDebounce.md
   */
  searching: false,
  /**
   * Error when searching
   */
  searchError: undefined,
  /**
   * Current search input value
   */
  searchQuery: '',
}

const reducer = (
  state: RecipientSelectState,
  action: RecipientSelectAction
): RecipientSelectState => {
  switch (action.type) {
    case 'update-search-query': {
      // We want to deselect the recipient every
      // time we get new search results
      let id: string | undefined = ''
      const currLength = state.searchQuery.length
      const incLength = action.query.length
      if ((currLength === 0 && incLength === 1) || (currLength === 1 && incLength === 0)) {
        // These are the only cases we don't deselect
        // because we don't get any results when we search
        // < 2 characters
        id = state.selectedRadioCosId
      }

      return {
        ...state,
        searchQuery: action.query,
        searching: action.searching,
        selectedRadioCosId: id,
      }
    }
    case 'toggle-searching':
      return { ...state, searching: action.searching }
    case 'set-search-error':
      return { ...state, searchError: action.error, searching: false }
    case 'reset':
      return initialState
    case 'select-radio-recipient':
      return { ...state, selectedRadioCosId: action.cosId }
    default:
      throw new Error(`Unexpected recipient select reducer action: ${JSON.stringify(action)}`)
  }
}

const [useRecipientSelect, useRecipientSelectOpsDispatch, ReducerProvider] = createReducerContext(
  reducer,
  initialState,
  'RecipientSelectProvider'
)

const useSafeSelectOpsDispatch = () => {
  const isMounted = useMountedState()
  const dispatch = useRecipientSelectOpsDispatch()
  return useCallback(
    (action: RecipientSelectAction) => {
      isMounted() && dispatch(action)
    },
    [dispatch, isMounted]
  )
}

const RecipientSelectInit: FC<{ children?: ReactNode }> = ({ children }) => {
  const { currentRecipient } = useRecipient()
  const dispatch = useRecipientSelectOpsDispatch()

  useEffect(() => {
    if (currentRecipient) {
      // Preselect the detected recipient on the recipient page
      dispatch({ type: 'select-radio-recipient', cosId: currentRecipient })
    }
  }, [currentRecipient, dispatch])

  return <>{children}</>
}

const RecipientSelectProvider: FC<{ children?: ReactNode }> = ({ children }) => (
  <ReducerProvider>
    <RecipientSelectInit>{children}</RecipientSelectInit>
  </ReducerProvider>
)

export { useRecipientSelect, useRecipientSelectOpsDispatch, useSafeSelectOpsDispatch }
export default RecipientSelectProvider
